import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App as AntApp, message as staticMessage } from "antd";
import ApprovalPanel from "./components/ApprovalPanel";
import ChatHeader from "./components/ChatHeader";
import Composer from "./components/Composer";
import MemoryConfirmation from "./components/MemoryConfirmation";
import MessageList, { type MessageListHandle } from "./components/MessageList";
import ModelSwitchModal from "./components/ModelSwitchModal";
import TodoPanel from "./components/TodoPanel";
import { useAgentChat } from "./hooks/useAgentChat";
import { I18nProvider, useT } from "./i18n/context";
import type { Locale, Messages } from "./i18n/types";
import type {
  AdapterType,
  AgentChatError,
  AgentMeta,
  AgentTransport,
  CodeBlockRenderer,
  ProviderInfo,
  SessionMeta,
} from "./types";

export interface AgentChatProps {
  // ─── 必需 ───
  transport: AgentTransport;
  agentId: string;
  sessionId: string;
  agent: AgentMeta;

  // ─── 可选展示元数据 ───
  session?: SessionMeta;

  // ─── i18n ───
  locale?: Locale;
  messages?: Partial<Messages>;

  // ─── 行为回调 ───
  onUploadAttachment?: (file: File) => Promise<string>;
  onSwitchModelSuccess?: (
    sessionId: string,
    adapterType: AdapterType,
    providerId: string,
    model: string,
  ) => void;
  onAgentExited?: (sessionId: string, reason?: string) => void;

  // ─── 多会话追踪 ───
  allSessionIds?: string[];
  onUnreadChange?: (state: { counts: Record<string, number>; activity: Record<string, boolean> }) => void;

  // ─── 功能开关（默认全开） ───
  showModelSwitcher?: boolean;
  showTodoPanel?: boolean;
  maxMessages?: number;

  // ─── 代码块自定义渲染 ───
  codeBlockRenderers?: Record<string, CodeBlockRenderer>;

  // ─── 错误处理 ───
  onError?: (err: AgentChatError) => void;
  silentError?: boolean;
}

export function AgentChat(props: AgentChatProps) {
  const { locale = "zh", messages: messageOverrides } = props;
  return (
    <I18nProvider locale={locale} messages={messageOverrides}>
      <AgentChatInner {...props} />
    </I18nProvider>
  );
}

function AgentChatInner({
  transport,
  agentId,
  sessionId,
  agent,
  session,
  locale = "zh",
  onUploadAttachment,
  onSwitchModelSuccess,
  onAgentExited,
  allSessionIds,
  onUnreadChange,
  showModelSwitcher = true,
  showTodoPanel: showTodoPanelProp = true,
  maxMessages = 200,
  codeBlockRenderers,
  onError,
  silentError = false,
}: AgentChatProps) {
  const t = useT();
  const listRef = useRef<MessageListHandle>(null);

  // antd v6 推荐通过 App 容器获取 message api；若未包裹则退回静态 API
  const antdApp = AntApp.useApp();
  const messageApi = antdApp?.message ?? staticMessage;

  const handleError = useCallback(
    (err: AgentChatError) => {
      onError?.(err);
      if (silentError) return;
      const text = errorText(t, err);
      messageApi.error(text);
    },
    [messageApi, onError, silentError, t],
  );

  const costTpl = t("cost.tpl");

  const {
    messages,
    todos,
    memoryConfirmations,
    firstItemIndex,
    loadingHistory,
    exited,
    send,
    approveTool,
    approveMemory,
    loadMoreHistory,
  } = useAgentChat({
    transport,
    agent,
    agentId,
    sessionId,
    allSessionIds,
    maxMessages,
    locale,
    costTpl,
    onError: handleError,
    onAgentExited,
    onUnreadChange,
  });

  // sticky-bottom：在底部时跟随新内容，不在底部时显示 floating button
  const lastMsg = messages[messages.length - 1];
  const scrollSig = lastMsg
    ? [
        lastMsg.id,
        lastMsg.content.length,
        lastMsg.thinking?.length ?? 0,
        (lastMsg.toolCalls ?? []).map((tc) => `${tc.toolUseId}:${tc.status}`).join(","),
      ].join("|")
    : "";
  useEffect(() => {
    if (messages.length > 0 && listRef.current?.isAtBottom()) {
      listRef.current.scrollToBottom();
    }
  }, [scrollSig, messages.length]);

  // session 切换 / 历史加载完成后滚到底部（多次重试确保 Virtuoso 渲染完成）
  const initialScrollDoneRef = useRef<string | null>(null);
  useEffect(() => {
    if (sessionId && messages.length > 0 && initialScrollDoneRef.current !== sessionId) {
      initialScrollDoneRef.current = sessionId;
      const scroll = () => listRef.current?.scrollToBottom();
      setTimeout(scroll, 100);
      setTimeout(scroll, 300);
      setTimeout(scroll, 600);
      setTimeout(scroll, 1000);
    }
  }, [sessionId, messages.length]);

  // ─── Provider 列表（用于 header 标签 + 模型切换 modal） ───
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const reloadProviders = useCallback(() => {
    transport
      .listProviders()
      .then(setProviders)
      .catch(() => {
        /* listProviders 失败一般不致命，由模型切换流程显式上报 */
      });
  }, [transport]);
  useEffect(() => {
    reloadProviders();
  }, [reloadProviders]);

  const [showModelModal, setShowModelModal] = useState(false);
  const [showTodoPopup, setShowTodoPopup] = useState(false);

  const pendingApprovals = useMemo(
    () =>
      messages.flatMap((m) =>
        (m.toolCalls ?? [])
          .filter((tc) => tc.needsApproval && tc.status === "pending")
          .map((tc) => ({
            toolUseId: tc.toolUseId,
            toolName: tc.toolName,
            input: tc.input,
          })),
      ),
    [messages],
  );

  const currentAdapterType: AdapterType = session?.sessionAdapterType ?? agent.adapterType;
  const currentProviderId = session?.sessionProviderId ?? "";
  const currentModel = session?.sessionModel ?? "";

  const handleModelSwitchError = useCallback(
    (cause: Error) => {
      handleError({ kind: "switch_model", cause });
    },
    [handleError],
  );

  const handleUploadError = useCallback(
    (file: File, cause: Error) => {
      handleError({ kind: "upload", file, cause });
    },
    [handleError],
  );

  const handleCodeBlockError = useCallback(
    (language: string, cause: Error) => {
      handleError({ kind: "code_block_render", language, cause });
    },
    [handleError],
  );

  return (
    <div
      className="lude-im"
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        background: "var(--lude-im-bg-primary)",
        color: "var(--lude-im-text-primary)",
        color: "var(--lude-im-text-primary)",
      }}
    >
      <ChatHeader
        agent={agent}
        session={session}
        providers={providers}
        showModelSwitcher={showModelSwitcher}
        onModelSwitcherClick={() => setShowModelModal(true)}
        todos={todos}
        showTodoPanel={showTodoPopup}
        onTodoTagClick={() => setShowTodoPopup((v) => !v)}
      />

      {showTodoPanelProp && (
        <TodoPanel
          todos={todos}
          open={showTodoPopup}
          onClose={() => setShowTodoPopup(false)}
        />
      )}

      <ApprovalPanel
        approvals={pendingApprovals}
        onApprove={(id) => approveTool(id, true)}
        onDeny={(id) => approveTool(id, false)}
      />

      <MemoryConfirmation
        confirmations={memoryConfirmations}
        onApprove={(id) => approveMemory(id, true)}
        onDeny={(id) => approveMemory(id, false)}
      />

      <MessageList
        ref={listRef}
        messages={messages}
        agentName={agent.name}
        firstItemIndex={firstItemIndex}
        loadingHistory={loadingHistory}
        onStartReached={loadMoreHistory}
        codeBlockRenderers={codeBlockRenderers}
        onCodeBlockRenderError={handleCodeBlockError}
      />

      <Composer
        disabled={false}
        onSend={(content, urls) => send(content, urls)}
        onUploadAttachment={onUploadAttachment}
        onUploadError={handleUploadError}
      />

      {showModelModal && showModelSwitcher && (
        <ModelSwitchModal
          open={showModelModal}
          transport={transport}
          sessionId={sessionId}
          providers={providers}
          currentAdapterType={currentAdapterType}
          currentProviderId={currentProviderId}
          currentModel={currentModel}
          onClose={() => setShowModelModal(false)}
          onSuccess={(sid, adapterType, providerId, model) => {
            onSwitchModelSuccess?.(sid, adapterType, providerId, model);
          }}
          onError={handleModelSwitchError}
        />
      )}
    </div>
  );
}

function errorText(t: ReturnType<typeof useT>, err: AgentChatError): string {
  switch (err.kind) {
    case "transport":
      return t("error.transport");
    case "agent_event":
      return err.message || t("error.unknown");
    case "upload":
      return t("error.upload");
    case "switch_model":
      return t("error.switch_model");
    case "history":
      return t("error.history");
    case "code_block_render":
      return t("error.code_block_render");
    default:
      return t("error.unknown");
  }
}
